import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Reveal } from "./Reveal";
import { Gift, Repeat, X, Loader2, CheckCircle2 } from "lucide-react";

const DEFAULT_OPTIONS = {
  carte: [
    { label: "Carte 20€", value: "20€" },
    { label: "Carte 35€", value: "35€" },
    { label: "Carte 50€", value: "50€" },
  ],
  abonnement: [
    { label: "Abonnement 3 ateliers — 80€", value: "80€" },
    { label: "Abonnement 5 ateliers — 100€", value: "100€" },
  ],
};

export default function GiftSection({ content }) {
  const OPTIONS = {
    carte: content?.gift_card_options?.length ? content.gift_card_options : DEFAULT_OPTIONS.carte,
    abonnement: content?.subscription_options?.length ? content.subscription_options : DEFAULT_OPTIONS.abonnement,
  };
  const [modal, setModal] = useState(null);
  const [option, setOption] = useState("");
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "", recipient_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const open = (kind) => {
    setModal(kind);
    setOption(OPTIONS[kind][0].label);
    setDone(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const opt = OPTIONS[modal].find((o) => o.label === option);
      const { data } = await api.post("/gift-orders", {
        kind: modal,
        option,
        value: opt?.value || option,
        ...form,
      });
      setDone(data);
    } catch (err) {
      setError(apiError(err, "Impossible d'enregistrer la commande"));
    } finally {
      setSubmitting(false);
    }
  };

  const cards = [
    {
      kind: "carte",
      icon: Gift,
      title: "Cartes Cadeaux",
      text: "Offrez un atelier créatif : trois montants au choix (20€, 35€ ou 50€). Valable 1 an, avec un joli mot personnalisé.",
      testid: "gift-card-carte",
    },
    {
      kind: "abonnement",
      icon: Repeat,
      title: "Abonnements",
      text: "Pour nos passionnés : 3 ou 5 ateliers, café offert à chaque séance et accès prioritaire aux nouveautés.",
      testid: "gift-card-abonnement",
    },
  ];

  return (
    <section id="cadeaux" data-testid="gift-section" className="py-20 md:py-28 bg-[#201815]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12">
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#D9822B] mb-4">Offrir & s'abonner</p>
          <h2 className="font-display tracking-tight text-[#FBF8F3] text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Le cadeau qui <span className="italic text-[#D9822B]">se peint.</span>
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {cards.map((c, i) => (
            <Reveal key={c.kind} delay={i * 0.1}>
              <article data-testid={c.testid} className="group relative rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-10 overflow-hidden h-full">
                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#C85A32]/10 blur-2xl transition-opacity duration-500 group-hover:opacity-100 opacity-50" />
                <c.icon size={28} className="text-[#D9822B] mb-6" />
                <h3 className="font-display text-2xl md:text-3xl text-[#FBF8F3] mb-3">{c.title}</h3>
                <p className="text-sm text-[#FBF8F3]/60 leading-relaxed mb-8 max-w-md">{c.text}</p>
                <button
                  data-testid={`${c.testid}-order-btn`}
                  onClick={() => open(c.kind)}
                  className="rounded-full border border-[#D9822B]/50 px-6 py-3 text-sm text-[#D9822B] transition-colors duration-300 hover:bg-[#D9822B] hover:text-[#201815]"
                >
                  Commander — paiement sur place
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#201815]/70 backdrop-blur-sm p-4"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="gift-order-modal"
              className="w-full max-w-lg rounded-3xl bg-[#FBF8F3] p-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {done ? (
                <div className="text-center py-6" data-testid="gift-order-confirmation">
                  <CheckCircle2 size={44} className="mx-auto text-[#4E7C59] mb-5" />
                  <h3 className="font-display text-2xl text-[#201815] mb-2">Commande enregistrée !</h3>
                  <p className="text-sm text-[#5C4E48] leading-relaxed">
                    Référence <span className="font-mono-brand text-[#C85A32]">{done.code}</span> — nous vous attendons au café pour le règlement et la remise de votre {modal === "carte" ? "carte cadeau" : "abonnement"}. Un email récapitulatif vient d'être envoyé.
                  </p>
                  <button
                    data-testid="gift-order-close-btn"
                    onClick={() => setModal(null)}
                    className="mt-7 rounded-full bg-[#201815] text-[#FBF8F3] px-7 py-3 text-sm"
                  >
                    Fermer
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="font-display text-2xl text-[#201815]">
                        {modal === "carte" ? "Commander une carte cadeau" : "Souscrire un abonnement"}
                      </h3>
                      <p className="text-xs text-[#8A7971] mt-1">Réservation en ligne — règlement au café.</p>
                    </div>
                    <button data-testid="gift-modal-close" onClick={() => setModal(null)} className="h-9 w-9 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#5C4E48]">
                      <X size={15} />
                    </button>
                  </div>

                  <form onSubmit={submit} className="space-y-3.5" data-testid="gift-order-form">
                    <select
                      data-testid="gift-option-select"
                      value={option}
                      onChange={(e) => setOption(e.target.value)}
                      className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] focus:border-[#C85A32] focus:outline-none"
                    >
                      {OPTIONS[modal].map((o) => (
                        <option key={o.label} value={o.label}>{o.label}</option>
                      ))}
                    </select>
                    <input data-testid="gift-buyer-name" required minLength={2} placeholder="Votre nom & prénom" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none" />
                    <input data-testid="gift-buyer-email" required type="email" placeholder="Votre email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none" />
                    <input data-testid="gift-buyer-phone" required minLength={6} placeholder="Votre téléphone" value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none" />
                    {modal === "carte" && (
                      <input data-testid="gift-recipient-name" placeholder="Nom du bénéficiaire (optionnel)" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none" />
                    )}
                    <textarea data-testid="gift-message" rows={2} placeholder="Un petit mot à joindre ?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none resize-none" />

                    {error && (
                      <p data-testid="gift-order-error" className="rounded-xl border border-[#C85A32]/40 bg-[#C85A32]/10 px-4 py-3 text-sm text-[#A84623]">{error}</p>
                    )}

                    <button
                      data-testid="gift-order-submit"
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-full bg-[#C85A32] py-4 text-sm font-medium text-[#FBF8F3] transition-colors duration-300 hover:bg-[#A84623] disabled:opacity-40 flex items-center justify-center gap-2"
                    >
                      {submitting && <Loader2 size={15} className="animate-spin" />}
                      Enregistrer ma commande
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
